import { useEffect, useState } from 'react';
import { ACTION_TYPES, useBudgetActionSearchDispatch, useBudgetActionSearchState } from './budget-action-search-context';
import BudgetActionAppService from '../../../../application/platform/sge/budget-action/budget-action-app-service';
import BudgetProgramAppService from '../../../../application/platform/sge/budget-program/budget-program-app-service';
import BudgetUnitAppService from '../../../../application/platform/sge/budget-unit/budget-unit-app-service';
import { useFeedback } from '../../../../hooks/use-feedback';
import { IBudgetActionAddEditRequest } from '../../../../model/sge/budget-action/request/budget-action-add-edit-request';
import { IBudgetActionResponse } from '../../../../model/sge/budget-action/response/budget-action-response';
import { parseNumberInput } from '../../../../utils/format';
import { formatCodeName } from '../../components/code-name-label';

export const useBudgetActionSearchController = () => {
    const dispatch = useBudgetActionSearchDispatch();
    const state = useBudgetActionSearchState();
    const { showSuccess, showError, confirmDelete, withLoading } = useFeedback();
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        Promise.all([BudgetProgramAppService.list(), BudgetUnitAppService.list()])
            .then(([budgetPrograms, budgetUnits]) => dispatch({ type: ACTION_TYPES.setLookups, payload: { budgetPrograms, budgetUnits } }))
            .catch((error: any) => showError('Não foi possível carregar os programas e unidades orçamentárias', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filtros
    function handleOnChange(e: any) {
        const { name, value } = e.target;
        if (name === 'select-budget-program') {
            dispatch({ type: ACTION_TYPES.updateParameter, payload: { budgetProgramId: parseNumberInput(value) } });
        } else if (name === 'input-search') {
            dispatch({ type: ACTION_TYPES.updateParameter, payload: { search: value || undefined } });
        }
    }

    function handleSearch() {
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    function handleClearFilters() {
        dispatch({ type: ACTION_TYPES.updateParameter, payload: { budgetProgramId: undefined, search: undefined } });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    // Cadastro/edição: a unidade orçamentária vem do programa da ação, para preencher a cascata
    async function openDrawer(row?: IBudgetActionResponse) {
        if (!row) {
            dispatch({ type: ACTION_TYPES.openDrawer });
            return;
        }
        try {
            const payload = await withLoading(async () => {
                const record = await BudgetActionAppService.getById(row.id);
                const program = await BudgetProgramAppService.getById(record.budgetProgramId);
                return { record, budgetUnitId: program.budgetUnitId };
            });
            dispatch({ type: ACTION_TYPES.openDrawer, payload });
        } catch (error: any) {
            showError('Não foi possível carregar a ação orçamentária', error);
        }
    }

    function closeDrawer() {
        dispatch({ type: ACTION_TYPES.closeDrawer });
    }

    async function handleSave(request: IBudgetActionAddEditRequest) {
        const editing = state.drawer.record;
        setIsSaving(true);
        try {
            if (editing) {
                await BudgetActionAppService.edit(editing.id, request);
                showSuccess('Ação orçamentária atualizada com sucesso!');
            } else {
                await BudgetActionAppService.add(request);
                showSuccess('Ação orçamentária adicionada com sucesso!');
            }
            closeDrawer();
            dispatch({ type: ACTION_TYPES.triggerSearch });
        } catch (error: any) {
            showError('Não foi possível salvar a ação orçamentária', error);
        } finally {
            setIsSaving(false);
        }
    }

    async function removeBudgetAction(row: IBudgetActionResponse) {
        try {
            await BudgetActionAppService.remove(row.id);
            showSuccess('Ação orçamentária excluída com sucesso!');
            dispatch({ type: ACTION_TYPES.triggerSearch });
        } catch (error: any) {
            showError('Não foi possível excluir a ação orçamentária', error);
        }
    }

    function handleDelete(row: IBudgetActionResponse) {
        confirmDelete(`Tem certeza que deseja excluir a ação orçamentária "${formatCodeName(row)}"?`, () => { removeBudgetAction(row); });
    }

    return { isSaving, handleOnChange, handleSearch, handleClearFilters, openDrawer, closeDrawer, handleSave, handleDelete };
};
